import { ClientTaxPayments } from './client-tax-payments'
import { ClientPayInvoiceCard } from './client-pay-invoice-card'
import {
  Banner,
  CaretDownIcon,
  Button,
  Modal,
  QuestionnaireBanner,
  QuestionnaireDashboardContainer
} from '@st/theme'
import { selClientOrganizerDashboard, selClientOrganizerLogout } from './client-organizer-selectors'
import { match, P } from '@st/pattern'
import {
  selClientPerspectiveDocumentChecklistItems,
  selMembershipSide,
  selFolderState,
  selFolderMissingReasonOptionsByConstraintId,
  selClientPerspectiveNonDocumentChecklistItems,
  selQuestionnaireDashboard
} from '~/features/st-folder-viewer/st-folder-selectors'
import { formatFolderEntityName } from '~/features/st-folder-viewer/st-folder-display'
import { useProcess, useSelect } from '~/store'
import { ClientQuestionnaireCard } from './client-questionnaire-card'
import { ClientUploadDocumentsCard } from './client-upload-documents-card'
import { SubmitClientOrganizerDialog } from './submit-client-organizer-dialog'
import { Folder } from '@st/sdk'
import { ClientContractCard } from '~/features/contract-signing/client-contract-card'
import { ClientOrganizerSaveIndicator } from './client-organizer-save-indicator'
import { ClientCard, ClientCardStepHeading } from './ui'

export function ClientOrganizerDashboard() {
  const auth = useProcess('auth')
  const stFolder = useProcess('stFolder')
  const route = useProcess('route')

  const folderState = useSelect('stFolder', selFolderState)
  const logoutMessage = useSelect('auth', selClientOrganizerLogout, folderState)
  const model = useSelect('stFolder', selClientOrganizerDashboard)
  const membershipSide = useSelect('stFolder', selMembershipSide)
  const dashboardState = useSelect('stFolder', selQuestionnaireDashboard)
  const nonDocumentChecklistItems = useSelect(
    'stFolder',
    selClientPerspectiveNonDocumentChecklistItems
  )
  const missingReasonOptionsByConstraintIdSubmit = useSelect(
    'stFolder',
    selFolderMissingReasonOptionsByConstraintId
  )
  const documentChecklistItemsSubmit = useSelect(
    'stFolder',
    selClientPerspectiveDocumentChecklistItems
  )

  if (!folderState || !model) {
    return null
  }

  const { organization, folder } = folderState
  const {
    isArchived,
    documentUploadsEnabled,

    groupEarlierSteps
  } = model

  const banner = match(model.banner)
    .with('archived', () => (
      <Banner variant="warning" className="rounded-none border-b border-stone-200">
        {`This has been archived and read-only. Please contact your accountant for assistance.`}
      </Banner>
    ))
    .with('submitted', () => (
      <div className="h-12">
        <QuestionnaireBanner variant="success">
          {organization.name} has received your information.
          {documentUploadsEnabled && ' You can upload additional documents at any time.'}
        </QuestionnaireBanner>
      </div>
    ))
    // Completing the questionnaire does not submit the organizer.
    .with('questionnaireCompleted', () => (
      <div className="h-12">
        <QuestionnaireBanner variant="warning">
          {`Once you've ${match(documentUploadsEnabled)
            .with(true, () => 'finished uploading all your documents')
            .with(false, () => 'completed the remaining steps')
            .exhaustive()}, click the Submit button below.`}
        </QuestionnaireBanner>
      </div>
    ))
    .otherwise(() => null)

  const renderStep = (step: (typeof model.earlierSteps)[number]) =>
    match(step)
      .with({ kind: 'contract' }, (step) => (
        <ClientContractCard
          key={step.id}
          stepNumber={step.stepNumber}
          folder={folder}
          source={step.contract}
          paymentRequired={step.paymentRequired}
          isArchived={isArchived}
        />
      ))
      .with({ kind: 'invoice' }, (step) => (
        <ClientPayInvoiceCard
          key={step.id}
          stepNumber={step.stepNumber}
          paidLabel={step.paidLabel}
          archived={step.archived}
          onSetStatus={(status) =>
            stFolder.send({
              type: 'requestEnqueued',
              request: {
                type: 'folders/setClientOrganizerInvoiceStatus',
                folderId: folder.id,
                status
              }
            })
          }
          onPay={() => route.send({ type: 'openUrl', url: step.url })}
        />
      ))
      .with({ kind: 'questionnaire' }, (step) => (
        <ClientQuestionnaireCard
          key={step.id}
          stepNumber={step.stepNumber}
          folder={folder}
          documentUploadsEnabled={documentUploadsEnabled}
          onClickEnterQuestionnaire={() =>
            stFolder.send({ type: 'questionnaire', message: { type: 'enterQuestionnaire' } })
          }
          isLocked={step.locked}
        />
      ))
      .with({ kind: 'documents' }, (step) => (
        <ClientUploadDocumentsCard
          key={step.id}
          stepNumber={step.stepNumber}
          isLocked={isArchived}
        />
      ))
      .with({ kind: 'payments' }, (step) => (
        <ClientTaxPayments key={step.id} stepNumber={step.stepNumber} model={step.model} />
      ))
      .with({ kind: 'submit' }, (step) => (
        <ClientCard key={step.id}>
          <ClientCardStepHeading
            stepNumber={step.stepNumber}
            completed={step.completed}
            title={<h2 className="text-xl font-semibold text-gray-900">Submit</h2>}
            status={<span className="text-base leading-6">{step.status}</span>}
            action={
              <Button
                size="l"
                className="min-w-24"
                variant="primary"
                disabled={!step.canSubmit}
                onClick={() =>
                  stFolder.send({
                    type: 'questionnaire',
                    message: { type: 'openSubmitDialog' }
                  })
                }
              >
                {step.label}
              </Button>
            }
          />
        </ClientCard>
      ))
      .exhaustive()

  const earlierSteps = (
    <div className="flex flex-col gap-2">
      <div className="mb-5 w-full whitespace-pre-wrap rounded-lg bg-yellow-100 px-6 py-5 text-lg leading-6 text-gray-800">
        {folderState.clientOrganizerDashboardNote}
      </div>
      {model.earlierSteps.map(renderStep)}
    </div>
  )

  return (
    <>
      <QuestionnaireDashboardContainer
        banner={banner}
        header={
          <header className="sticky top-0 flex h-12 w-full flex-row items-center justify-between border-b bg-white px-5 sm:px-14">
            {organization.name}
            <div className="flex flex-row items-center gap-3">
              <ClientOrganizerSaveIndicator />
              {membershipSide == 'client' && (
                <Button variant="link" onClick={() => auth.send(logoutMessage)}>
                  Logout
                </Button>
              )}
            </div>
          </header>
        }
      >
        <div className="text-center">
          <h1 className="text-3xl font-semibold leading-tight text-gray-900">
            Tax Year {folder.year}
          </h1>
          <div className="mt-2 text-lg text-gray-600">{formatClientOrganizerSubtitle(folder)}</div>
        </div>

        <div className="mt-7 flex flex-col gap-2">
          {match(groupEarlierSteps)
            .with(true, () => (
              <>
                <details key={folder.id} className="group">
                  <summary className="mb-3 flex cursor-pointer list-none items-center gap-4 text-sm text-gray-600 [&::-webkit-details-marker]:hidden">
                    <span className="font-semibold">Earlier steps</span>
                    <span className="h-px flex-1 bg-gray-300" />
                    <span className="group-open:hidden">Show</span>
                    <span className="hidden group-open:inline">Hide</span>
                    <CaretDownIcon className="size-4 group-open:rotate-180" />
                  </summary>
                  {earlierSteps}
                </details>
                <div className="mb-3 mt-4 flex items-center gap-4 text-sm font-semibold text-gray-600">
                  <h2>Your tax return</h2>
                  <span className="h-px flex-1 bg-gray-300" />
                </div>
              </>
            ))
            .with(false, () => earlierSteps)
            .exhaustive()}
          {model.taxReturnSteps.map(renderStep)}
        </div>
      </QuestionnaireDashboardContainer>

      {match(dashboardState)
        .with({ status: P.union('validating', 'open', 'submitting') }, (submitDialog) => (
          <Modal isOpen={true}>
            <SubmitClientOrganizerDialog
              documentUploadsEnabled={documentUploadsEnabled}
              submitDialog={submitDialog}
              documentChecklistItems={documentChecklistItemsSubmit}
              nonDocumentChecklistItems={nonDocumentChecklistItems}
              missingReasonOptionsByConstraintId={missingReasonOptionsByConstraintIdSubmit}
              onCancel={() =>
                stFolder.send({ type: 'questionnaire', message: { type: 'closeSubmitDialog' } })
              }
              onSubmit={(incompleteReasonOptions) => {
                stFolder.send({
                  type: 'questionnaire',
                  message: {
                    type: 'submitClientOrganizer',
                    forceSubmit: false,
                    incompleteReasonOptions
                  }
                })
              }}
            />
          </Modal>
        ))
        .otherwise(() => null)}
    </>
  )
}

function formatClientOrganizerSubtitle(folder: Folder) {
  const entityName = formatFolderEntityName(folder.entities, 'client')
  if (entityName) {
    return `${entityName} · ${folder.type}`
  }
  return folder.type
}
